import React from "react";
import { BetButtons, useMediaQuery, breakpoints } from "@tvg/design-system";
import { WagerTypeCodesEnum } from "@tvg/ts-types/Wager";
import {
  HorseChanges,
  RaceWagerType,
  RaceProgram,
  JockeyChange,
  RaceTypeCodeEnum
} from "@tvg/ts-types/Race";
import ApolloContext from "@tvg/utils/apolloContext";
import ApolloContextClients from "@tvg/utils/types/ApolloContext";
import type { UnaryFn } from "@tvg/ts-types/Functional";
import { RunnerBI } from "../../hooks/useGetRunnerFromBI";
import { openLoginWall } from "../../utils/general";
import { RunnerModifierEnum } from "../../types";
import { Container, MobileBetButtonsContainer } from "./styled-components";
import RunnerInfo from "./components/RunnerInfo";
import AdvancedInfo from "./components/AdvancedInfo";
import PerformanceInfo from "./components/PerformanceInfo";
import RunnerHeaderInfo from "./components/RunnerHeaderInfo";
import { BestPerformanceStats } from "../RunnerList/types";

export interface RunnerProps {
  isDriver: boolean;
  wagerType?: RaceWagerType;
  runnerBI: RunnerBI;
  onClickBetButton: (
    type: string,
    isActive: boolean,
    position?: number
  ) => void;
  hasBorderTop?: boolean;
  unSelectBetButtons?: boolean;
  activeButtons: boolean[];
  isWagerable: boolean;
  currentRace: RaceProgram;
  changes?: HorseChanges | null;
  trackDataSource: string | undefined;
  isLogged: boolean;
  jockeyChanges: JockeyChange[];
  runnerModifier: RunnerModifierEnum;
  isInfoOpen?: boolean;
  onInfoOpenToggle?: (biNumber: string) => void;
  bestPerformanceRunners?: BestPerformanceStats;
  hasRunnerSilk: boolean;
  isScrollableRacecard?: boolean;
  allRunnersHasSilksError?: boolean;
  handleError?: UnaryFn<number, void>;
  showSilks?: boolean;
}

const Runner = ({
  isDriver = false,
  hasBorderTop = true,
  runnerBI,
  wagerType,
  onClickBetButton,
  activeButtons,
  isWagerable = true,
  trackDataSource,
  currentRace,
  changes,
  isLogged = false,
  jockeyChanges,
  runnerModifier = RunnerModifierEnum.DEFAULT,
  isInfoOpen = false,
  onInfoOpenToggle,
  bestPerformanceRunners = {},
  hasRunnerSilk,
  allRunnersHasSilksError,
  handleError,
  showSilks
}: RunnerProps) => {
  const isDesktop = useMediaQuery(breakpoints.tablet.min.sm);
  const columnCount = wagerType?.columnCount || 1;

  const onLogin = () => {
    if (typeof onInfoOpenToggle === "function") {
      onInfoOpenToggle(runnerBI.number);
    }
  };

  const onRunnerTitleClick = () => {
    if (isLogged) {
      if (typeof onInfoOpenToggle === "function")
        onInfoOpenToggle(runnerBI.number);
    } else openLoginWall(onLogin);
  };

  const isWPSBetType =
    wagerType?.type?.code === WagerTypeCodesEnum.WIN_PLACE ||
    wagerType?.type?.code === WagerTypeCodesEnum.WIN_PLACE_SHOW;

  return (
    <Container
      hasBorderTop={hasBorderTop}
      data-qa-label={`race-card-container-${runnerBI.number}`}
    >
      <RunnerHeaderInfo
        runnerBI={runnerBI}
        isInfoOpen={isInfoOpen}
        isDesktop={isDesktop}
        isDriver={isDriver}
        onClickBetButton={onClickBetButton}
        activeButtons={activeButtons}
        wagerType={wagerType}
        onRunnerTitleClick={onRunnerTitleClick}
        isLogged={isLogged}
        jockeyChanges={jockeyChanges}
        isWagerable={isWagerable}
        isAmericanRace={currentRace.track?.trackLocation?.country === "USA"}
        raceTypeCode={currentRace.type?.code || RaceTypeCodeEnum.ALL}
        allRunnersHasSilksError={allRunnersHasSilksError}
        handleError={handleError}
        trackDataSource={trackDataSource}
        showSilks={showSilks}
        hasRunnerSilk={hasRunnerSilk}
      />
      {runnerModifier === RunnerModifierEnum.ADVANCED &&
        isLogged &&
        !isInfoOpen && (
          <AdvancedInfo
            runnerInfo={runnerBI}
            isScratched={runnerBI.isScratched}
            isWPSBetType={isWPSBetType}
            bestPerformanceRunners={bestPerformanceRunners}
            isDesktop={isDesktop}
            hasSilk={hasRunnerSilk}
          />
        )}
      {runnerModifier === RunnerModifierEnum.PERFORMANCE &&
        isLogged &&
        !isInfoOpen && (
          <PerformanceInfo
            runnerInfo={runnerBI}
            isScratched={runnerBI.isScratched}
            isDriver={isDriver}
            bestPerformanceRunners={bestPerformanceRunners}
            hasSilk={hasRunnerSilk}
          />
        )}
      <ApolloContext.Consumer>
        {(clients: ApolloContextClients) => (
          <RunnerInfo
            isOpen={isInfoOpen}
            currentRace={currentRace}
            entityRunnerId={runnerBI.entityRunnerId}
            runnerNumber={runnerBI.number}
            runnerName={runnerBI.runnerName}
            runnerDob={runnerBI.runnerDob}
            runnerBI={runnerBI}
            fcpClient={clients.fcpClient}
            changes={changes}
            trackDataSource={trackDataSource}
            timeform={runnerBI.timeform}
            handicapping={runnerBI.handicapping}
            isWPSBetType={isWPSBetType}
            bestPerformanceRunners={bestPerformanceRunners}
            hasSilk={hasRunnerSilk}
          >
            {isWagerable &&
            !runnerBI.isScratched &&
            !isDesktop &&
            columnCount > 1 ? (
              <MobileBetButtonsContainer hasOrder={false}>
                <BetButtons
                  wagerType={wagerType}
                  onPress={onClickBetButton}
                  activeButtons={activeButtons}
                />
              </MobileBetButtonsContainer>
            ) : null}
          </RunnerInfo>
        )}
      </ApolloContext.Consumer>
      {/* Show bets button on this position if is mobile,
          wagerable, bet type has more than one button and info is closed */}
      {isWagerable &&
        !runnerBI.isScratched &&
        !isDesktop &&
        columnCount > 1 &&
        !isInfoOpen && (
          <MobileBetButtonsContainer hasOrder>
            <BetButtons
              wagerType={wagerType}
              onPress={onClickBetButton}
              activeButtons={activeButtons}
            />
          </MobileBetButtonsContainer>
        )}
    </Container>
  );
};

export default Runner;
